import { useState, useEffect, useMemo } from "react";
import axiosInstance from "../../../../Utils/axiosInstance";
import { getPreviousMonthYear, DEFAULT_YEAR, defaultData, buildDashboardData, getConsolidatedReportLink } from "./utils";
import { getLineChartOption, getBarChartOption, getDonutChartOption } from "./chartConfigs";

export const useCashInDashboard = (location) => {
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [months, setMonths] = useState([]);
  const [selectedYear, setSelectedYear] = useState(DEFAULT_YEAR);
  const [dashboardData, setDashboardData] = useState(defaultData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [apiCurrentMonth, setApiCurrentMonth] = useState(null);
  const [apiCurrentYear, setApiCurrentYear] = useState(null);

  const { dashboard, selectedProcess } = location.state || {};

  useEffect(() => {
    const fetchMonths = async () => {
      try {
        const response = await axiosInstance.get("/eda3/eda3cashinmonths");
        if (response?.data?.status) {
          setMonths(response.data.items);
          const currentMonth = response.data.items.find((item) =>
            item.label.startsWith("Current Month")
          );
          if (currentMonth) {
            setSelectedMonth(currentMonth.value);
            setApiCurrentMonth(currentMonth.value);
          }
        }
      } catch (error) {
        console.error("Failed to fetch months", error);
      }
    };
    fetchMonths();
  }, []);

  useEffect(() => {
    if (!location.state || !selectedMonth) return;
    let ignore = false;
    const fetchData = async () => {
      setLoading(true);
      setError("");
      try {
        const prev = getPreviousMonthYear(selectedMonth, selectedYear);
        const [response, prevResponse] = await Promise.all([
          axiosInstance.post("/eda3/eda3cashIn", { Month: selectedMonth, Year: selectedYear }),
          axiosInstance.post("/eda3/eda3cashIn", { Month: prev.month, Year: prev.year }).catch(() => null),
        ]);

        if (!ignore && response?.data?.status && response?.data?.items) {
          const built = buildDashboardData(response.data.items);
          const prevItems = prevResponse?.data?.status ? prevResponse.data.items : null;
          built.chart2Prev = prevItems?.chart2 || [];
          built.chart3Prev = prevItems?.chart3 || [];
          setDashboardData(built);
        }
      } catch (err) {
        if (!ignore) setError(err?.response?.data?.message || "Failed to fetch Cash In data.");
      } finally {
        if (!ignore) setLoading(false);
      }
    };
    fetchData();
    return () => { ignore = true; };
  }, [location.state, selectedMonth, selectedYear]);

  const consolidatedReportLink = useMemo(() => getConsolidatedReportLink(dashboard), [dashboard]);

  const lineChartOption = useMemo(() => getLineChartOption(dashboardData.chart1), [dashboardData.chart1]);
  const barChart2Option = useMemo(() => getBarChartOption(dashboardData.chart2, dashboardData.chart2Prev, "PROJECT", "Value", "#0060D0", "Onshore Project-wise"), [dashboardData.chart2, dashboardData.chart2Prev]);
  const barChart3Option = useMemo(() => getBarChartOption(dashboardData.chart3, dashboardData.chart3Prev, "Group", "Value", "#35a57f", "Service Group-wise"), [dashboardData.chart3, dashboardData.chart3Prev]);
  const donutChart4Option = useMemo(() => getDonutChartOption(dashboardData.chart4), [dashboardData.chart4]);

  const chart4Legend = useMemo(() =>
    dashboardData.chart4
      .filter((d) => !(d.Name || "").toLowerCase().includes("grand total"))
      .sort((a, b) => Number(b.Values || 0) - Number(a.Values || 0))
      .map((d, i) => ({
        name: d.Name, value: d.Values, color: ["#d97706", "#f59e0b", "#0060D0", "#6d4cf5", "#35a57f"][i % 5],
      })),
    [dashboardData.chart4]
  );

  return {
    selectedMonth, setSelectedMonth, months,
    selectedYear, setSelectedYear,
    dashboardData, loading, error,
    apiCurrentMonth, apiCurrentYear,
    dashboard, selectedProcess,
    consolidatedReportLink,
    lineChartOption, barChart2Option, barChart3Option, donutChart4Option, chart4Legend
  };
};
